import { useCallback } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  CATEGORY_GROUPS,
  PUBLIC_DOCS_DEFAULT_LIMIT,
} from '@listening/api-contract';
import { slugifyTitle } from '@listening/utils/slug-utils';
import {
  DocumentList,
  DocumentListSkeleton,
} from '@/components/astro/features/public-pdf-doc/document-list';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { publicDocSearchSchema } from '@/analytics/cacheable-route-search';
import { callApiPromise } from '@/runtimes/get-runtime';
import { normalizeCanonicalUrl } from '@/seo/canonical';

const PUBLIC_DOCS_QUERY_STALE_TIME_MS = 5 * 60 * 1000;

const publicDocCategoryBySlug = new Map(
  Object.keys(CATEGORY_GROUPS).map((category) => [
    getPublicDocCategorySlug(category),
    category,
  ]),
);

type PublicDocCategoryFacet = {
  category: string;
};

const defaultCategoryFacets: Array<PublicDocCategoryFacet> = Object.entries(
  CATEGORY_GROUPS,
).map(([category]) => ({
  category,
}));

export const DEFAULT_PUBLIC_DOC_CATEGORY =
  defaultCategoryFacets[0]?.category ??
  Object.keys(CATEGORY_GROUPS)[0] ??
  'Other';

// Lives in the leaf analytics module so the cache-key drift test can import
// it without dragging this component (and the runtime) along; re-exported
// here for the doc routes that already import it from this file.
export { publicDocSearchSchema };

export function getPublicDocCategorySlug(category: string) {
  return slugifyTitle(category);
}

export function getPublicDocCategoryFromSlug(slug: string) {
  return publicDocCategoryBySlug.get(slug);
}

export function getPublicDocCategoryHref(category: string) {
  return normalizeCanonicalUrl(`/doc/${getPublicDocCategorySlug(category)}`);
}

export function getPublicDocListHref(
  basePath: string,
  params: {
    page?: number;
    search?: string;
  },
) {
  const searchParams = new URLSearchParams();
  if (params.search) searchParams.set('search', params.search);
  if (params.page && params.page > 1) {
    searchParams.set('page', params.page.toString());
  }
  const query = searchParams.toString();
  return normalizeCanonicalUrl(`${basePath}${query ? `?${query}` : ''}`);
}

export function normalizePublicDocCategory(category?: string) {
  return category && category in CATEGORY_GROUPS
    ? category
    : DEFAULT_PUBLIC_DOC_CATEGORY;
}

export function getPublicDocCategoryDescription(category: string) {
  return `Browse ${category.toLowerCase()} documents, PDFs, articles, and research papers available on Listening.`;
}

export function getPublicDocCategoryIntro(category: string) {
  return [
    `${category} documents on Listening collect public PDFs, articles, papers, and references that are useful for reading, listening, and research workflows.`,
    `Browse available documents, open the full read-along page, or listen to the audio version when available. Each document page keeps the source material accessible with a stable URL for later reference.`,
  ];
}

export function publicDocsPageOptions(params: {
  page: number;
  cursor?: string;
  search?: string;
  category?: string;
}) {
  return queryOptions({
    queryKey: ['publicDocs', params] as const,
    staleTime: PUBLIC_DOCS_QUERY_STALE_TIME_MS,
    queryFn: () =>
      callApiPromise((api) =>
        api.PublicDoc.ListPublicDocs({
          query: {
            limit: PUBLIC_DOCS_DEFAULT_LIMIT,
            page: params.page,
            cursor: params.cursor,
            search: params.search,
            category: params.category,
          },
        }),
      ),
  });
}

export function PublicDocListPending({
  selectedCategory = DEFAULT_PUBLIC_DOC_CATEGORY,
}: {
  selectedCategory?: string;
}) {
  return (
    <>
      <NavBar sticky={false} />
      <main className='m-auto w-full max-w-[1600px] px-4 py-10 md:py-14'>
        <DocumentRepositoryHeader
          heading={`${selectedCategory} Documents`}
          description='Explore, download, and listen to PDFs, articles, and research documents grouped by topic.'
        />
        <div className='mx-auto max-w-[800px] space-y-4'>
          <DocumentListSkeleton />
        </div>
      </main>
    </>
  );
}

export function PublicDocListPage({
  selectedCategory,
  search,
  page,
  cursor,
  categoryFilter = selectedCategory,
  basePath,
  heading,
  description,
}: {
  selectedCategory: string | undefined;
  search?: string;
  page: number;
  cursor?: string;
  categoryFilter?: string;
  basePath: string;
  heading?: string;
  description?: string;
}) {
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const { data, isFetching } = useQuery(
    publicDocsPageOptions({
      page,
      cursor,
      search,
      category: categoryFilter,
    }),
  );
  const handleSearchChange = useCallback(
    (value: string) => {
      void navigate({
        to: '.',
        search: (prev: typeof publicDocSearchSchema.Type) => ({
          ...prev,
          search: value || undefined,
          category: undefined,
          page: undefined,
          cursor: undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      void navigate({
        to: '.',
        search: (prev: typeof publicDocSearchSchema.Type) => ({
          ...prev,
          category: undefined,
          page: nextPage > 1 ? nextPage : undefined,
          cursor: undefined,
        }),
      });
    },
    [navigate],
  );
  const handleCategoryChange = useCallback(
    (category: string) => {
      void navigate({
        to: '/doc/$docId',
        params: { docId: getPublicDocCategorySlug(category) },
        search: {},
      });
    },
    [navigate],
  );

  const items = data?.items ?? [];
  const responseCategoryFacets = data?.categoryFacets ?? [];
  const categoryFacets =
    responseCategoryFacets.length > 0
      ? responseCategoryFacets
      : defaultCategoryFacets;
  const totalPages = data?.totalPages ?? 1;
  const currentPage = data?.page ?? page;
  const showListSkeleton = isFetching && !data;
  const getPageHref = useCallback(
    (nextPage: number) => {
      return getPublicDocListHref(basePath, {
        search,
        page: nextPage,
      });
    },
    [basePath, search],
  );
  const topicIntro =
    !search && selectedCategory
      ? getPublicDocCategoryIntro(selectedCategory)
      : [];

  return (
    <>
      <NavBar sticky={false} />

      <main className='m-auto w-full max-w-[1600px] px-4 py-10 md:py-14'>
        <DocumentRepositoryHeader
          heading={
            heading ?? `${selectedCategory ?? 'Document Repository'} Documents`
          }
          description={
            description ??
            'Explore, download, and listen to PDFs, articles, and research documents grouped by topic.'
          }
        />

        <DocumentList
          model={{
            docs: items,
            heading: selectedCategory ?? 'Document Repository',
            navigation: {
              categoryFacets,
              selectedCategory,
              currentPath: pathname,
              getCategoryHref: getPublicDocCategoryHref,
              onCategoryChange: handleCategoryChange,
            },
            pagination: {
              page: currentPage,
              totalPages,
              getPageHref,
              onPageChange: handlePageChange,
            },
            search: {
              value: search ?? '',
              onChange: handleSearchChange,
            },
            state: {
              loading: showListSkeleton,
              pending: false,
            },
          }}
        />

        {topicIntro.length > 0 && (
          <PublicDocTopicIntro paragraphs={topicIntro} />
        )}
      </main>

      <Footer />
    </>
  );
}

function PublicDocTopicIntro({
  paragraphs,
}: {
  paragraphs: ReadonlyArray<string>;
}) {
  return (
    <details className='mx-auto mt-10 max-w-4xl border-t border-neutral-200 pt-4 text-sm leading-6 text-neutral-665'>
      <summary className='cursor-pointer text-sm font-medium text-neutral-700 hover:text-black'>
        About this collection
      </summary>
      <div className='mt-3 space-y-3'>
        {paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </details>
  );
}

function DocumentRepositoryHeader({
  heading,
  description,
}: {
  heading: string;
  description: string;
}) {
  return (
    <div className='mb-14 text-center'>
      <h1 className='mb-4 truncate text-3xl leading-tight font-semibold tracking-[-0.04em] text-black md:text-5xl lg:text-6xl'>
        {heading}
      </h1>
      <p className='mx-auto max-w-3xl text-lg font-semibold text-neutral-665 md:text-xl'>
        {description}
      </p>
    </div>
  );
}
